import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type CDPStatus = components['schemas']['CDPStatus']
export type CDPLaunchResponse = components['schemas']['CDPLaunchResponse']

/**
 * Polls ``GET /api/v1/scraping/cdp/status`` so the form can render the
 * "Chromium connected" badge live.
 *
 * The endpoint is a cheap probe (a 2s-timeout HTTP call to the local
 * debugging port) so 5s polling is fine — the page is also revalidated
 * on focus return and on every successful launch (we invalidate from
 * ``useStartCDPLaunch``). When the launch is in flight the status
 * doesn't refetch faster on its own; we accept the up-to-5s lag because
 * the launch mutation already returns the post-spawn health snapshot.
 */
export function useCDPStatus() {
  return useQuery({
    queryKey: queryKeys.scraping.cdpStatus(),
    queryFn: async (): Promise<CDPStatus> => {
      const { data } = await api.GET('/api/v1/scraping/cdp/status')
      if (!data) throw new Error('cdp status response empty')
      return data.data
    },
    refetchInterval: 5_000,
    // Don't hammer the backend when the user tabs away; refetch on
    // focus return is enough to catch external state changes (user
    // closed the Chromium window themselves, etc.).
    refetchIntervalInBackground: false,
    staleTime: 2_000,
  })
}

/**
 * Variables for the launch mutation.
 *
 * - ``force``: kill any existing Chromium bound to the profile and
 *   spawn fresh. Used by both "Reabrir" and "Abrir janela" so a mode
 *   swap (headless ↔ headed) actually takes effect.
 * - ``headless``: ``true``/``false`` overrides ``CDP_HEADLESS`` for
 *   this call. ``undefined`` falls back to the backend setting (keeps
 *   the routine "Conectar navegador" button mode-agnostic).
 */
export interface CDPLaunchVars {
  force?: boolean
  headless?: boolean
}

/**
 * Triggers ``POST /api/v1/scraping/cdp/launch`` — idempotent spawn of
 * the managed Chromium. ``data.launched_now`` is True only when this
 * call was the one that actually started the browser; the UI uses that
 * to choose between a "Browser opened, log in" toast and a silent
 * confirmation.
 *
 * The 503 path (auto-launch disabled or spawn timed out) bubbles up as
 * an ``ApiError`` from the global errorMiddleware — let the caller
 * surface ``mutation.error?.message`` in a toast.
 */
export function useStartCDPLaunch() {
  const qc = useQueryClient()
  return useMutation<CDPLaunchResponse, Error, CDPLaunchVars>({
    mutationFn: async ({ force, headless }) => {
      const query: { force?: boolean; headless?: boolean } = {}
      if (force !== undefined) query.force = force
      if (headless !== undefined) query.headless = headless
      const { data } = await api.POST('/api/v1/scraping/cdp/launch', {
        params: { query },
      })
      if (!data) throw new Error('cdp launch response empty')
      return data.data
    },
    onSuccess: () => {
      // Refresh the status query immediately so the badge flips to
      // green without waiting for the next 5s tick.
      void qc.invalidateQueries({ queryKey: queryKeys.scraping.all })
    },
  })
}
